import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChartSquare02Icon,
  AnnotationIcon,
  ChevronLeftIcon,
  MessageChatCircleIcon,
  Building07Icon,
  HeartHandIcon,
  Settings02Icon,
  Stars02Icon,
  MessageCircle01Icon,
  Menu01Icon,
  FileCode02Icon,
  ClipboardCheckIcon,
  UserPlus01Icon,
  Phone01Icon,
} from '@untitledui-icons/react/line'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useIdentity } from '@/hooks/api/useIdentity'
import { canAccessWidgetHub } from '@/hooks/api/usePermissions'
import { Logo } from './Logo'
import { UserMenu } from './UserMenu'
import { NavItemBase } from '@/components/application/app-navigation/base-components/nav-item'
import { NavItemButton } from '@/components/application/app-navigation/base-components/nav-item-button'
import { cn } from '@/lib/utils'
import type { FC, MouseEvent } from 'react'

interface NavItem {
  labelKey: string
  href: string
  icon: FC<{ className?: string }>
  permissions?: string[]
  badgeKey?: string
  /**
   * Custom visibility check function. If provided, takes precedence over permissions check.
   * Return true to show the item.
   */
  visibilityCheck?: (context: {
    role: string
    customerId?: number
    customerMeta?: Record<string, unknown>
    partnerId?: number
  }) => boolean
}

const mainNavItems: NavItem[] = [
  { labelKey: 'navigation.dashboard', href: '/dashboard', icon: BarChartSquare02Icon },
  { labelKey: 'navigation.notes', href: '/notes', icon: AnnotationIcon },
  { labelKey: 'navigation.conversations', href: '/conversations', icon: MessageChatCircleIcon },
  { labelKey: 'navigation.assistants', href: '/assistants', icon: Stars02Icon },
  {
    labelKey: 'navigation.whatsapp',
    href: '/widgets',
    icon: MessageCircle01Icon,
    badgeKey: 'navigation.beta',
    visibilityCheck: canAccessWidgetHub,
  },
  {
    labelKey: 'navigation.prompt_templates',
    href: '/prompt-templates',
    icon: FileCode02Icon,
    // Custom visibility: admin, partner, OR user with allowed_to_make_templates
    visibilityCheck: ({ role, customerMeta }) => {
      if (role === 'admin' || role === 'partner') return true
      if (role === 'user' && customerMeta?.allowed_to_make_templates === true) return true
      return false
    },
  },
]

const partnerNavItems: NavItem[] = [
  {
    labelKey: 'navigation.partners',
    href: '/partners',
    icon: HeartHandIcon,
    permissions: ['admin'],
  },
  {
    labelKey: 'navigation.customers',
    href: '/customers',
    icon: Building07Icon,
    permissions: ['admin', 'partner'],
  },
  {
    labelKey: 'navigation.signups',
    href: '/signups',
    icon: UserPlus01Icon,
    visibilityCheck: ({ role, partnerId }) => {
      if (role === 'admin') return true
      if (role === 'partner' && partnerId === 1) return true
      return false
    },
  },
  {
    labelKey: 'resources.onboarding.welcome',
    href: '/onboarding',
    icon: Settings02Icon,
    permissions: ['admin', 'partner'],
  },
  {
    labelKey: 'navigation.simulations',
    href: '/simulations',
    icon: ClipboardCheckIcon,
    visibilityCheck: ({ role }) => {
      // Feature flag - disabled in production until backend is deployed
      if (import.meta.env.VITE_ENABLE_SIMULATIONS !== 'true') return false
      return role === 'admin'
    },
  },
  {
    labelKey: 'navigation.phone_numbers',
    href: '/phone-numbers',
    icon: Phone01Icon,
    permissions: ['admin'],
  },
]

interface SidebarProps {
  /**
   * Mobile view mode (full drawer)
   */
  mobile?: boolean
}

/**
 * Check if the current path matches a nav item's href.
 * Matches exact path or any sub-path (e.g., /assistants matches /assistants/123).
 */
function isPathActive(pathname: string, href: string): boolean {
  if (pathname === href) return true
  // Check if the current path starts with href followed by a slash
  // This ensures /assistants matches /assistants/123 but not /assistantsX
  return pathname.startsWith(href + '/')
}

export function Sidebar({ mobile = false }: SidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const sidebarState = useUIStore((state) => state.sidebarState)
  const setSidebarState = useUIStore((state) => state.setSidebarState)
  const setMobileMenuOpen = useUIStore((state) => state.setMobileMenuOpen)
  const user = useAuthStore((state) => state.user)
  const { user: identityUser } = useIdentity()

  const isCollapsed = !mobile && sidebarState === 'collapsed'
  const userPermission = user?.role || 'user'
  const customerId = identityUser?.customer_id ?? user?.customerId
  const customerMeta = identityUser?.customer?.meta
  const partnerId = identityUser?.partner_id ?? user?.partnerId

  const toggleCollapse = () => {
    setSidebarState(isCollapsed ? 'expanded' : 'collapsed')
  }

  const handleNavClick = (e: MouseEvent, href: string) => {
    e.preventDefault()
    navigate(href)
    if (mobile) {
      setMobileMenuOpen(false)
    }
  }

  const filterByPermission = (items: NavItem[]) =>
    items.filter((item) => {
      // If custom visibility check provided, use it
      if (item.visibilityCheck) {
        return item.visibilityCheck({
          role: userPermission,
          customerId: customerId ? Number(customerId) : undefined,
          customerMeta,
          partnerId: partnerId ? Number(partnerId) : undefined,
        })
      }
      // Otherwise fall back to permissions array
      return !item.permissions || item.permissions.includes(userPermission)
    })

  const filteredMainNav = filterByPermission(mainNavItems)
  const filteredPartnerNav = filterByPermission(partnerNavItems)

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-[rgba(0,0,0,0.08)] bg-[var(--color-sidebar-bg)] transition-all duration-300 dark:border-gray-800 dark:bg-gray-900',
        mobile ? 'w-[280px]' : isCollapsed ? 'w-[72px]' : 'w-[240px]'
      )}
    >
      {/* Header with logo and toggle */}
      <div
        className={cn(
          'flex items-center border-b border-gray-100 p-4 dark:border-gray-800',
          isCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {isCollapsed ? (
          <button
            type="button"
            onClick={toggleCollapse}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('navigation.expand_sidebar', 'Expand sidebar')}
          >
            <Menu01Icon className="h-5 w-5" />
          </button>
        ) : (
          <>
            <Logo width={100} />
            {!mobile && (
              <button
                type="button"
                onClick={toggleCollapse}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                aria-label={t('navigation.collapse_sidebar', 'Collapse sidebar')}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
        {/* Main navigation */}
        <ul className="space-y-1">
          {filteredMainNav.map((item) => (
            <li key={item.href}>
              {isCollapsed ? (
                <NavItemButton
                  label={t(item.labelKey)}
                  icon={item.icon}
                  href={item.href}
                  current={isPathActive(location.pathname, item.href)}
                  size="md"
                  tooltipPlacement="right"
                  onClick={(e) => handleNavClick(e as unknown as MouseEvent, item.href)}
                />
              ) : (
                <NavItemBase
                  type="link"
                  href={item.href}
                  icon={item.icon}
                  badge={item.badgeKey ? t(item.badgeKey) : undefined}
                  current={isPathActive(location.pathname, item.href)}
                  onClick={(e) => handleNavClick(e as unknown as MouseEvent, item.href)}
                >
                  {t(item.labelKey)}
                </NavItemBase>
              )}
            </li>
          ))}
        </ul>

        {/* Partner tools section */}
        {filteredPartnerNav.length > 0 && (
          <>
            <div className="my-3 border-t border-gray-100 dark:border-gray-800" />

            {!isCollapsed && (
              <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('resources.generic.partner_tools', 'Partner Tools')}
              </p>
            )}

            <ul className="space-y-1">
              {filteredPartnerNav.map((item) => (
                <li key={item.href}>
                  {isCollapsed ? (
                    <NavItemButton
                      label={t(item.labelKey)}
                      icon={item.icon}
                      href={item.href}
                      current={isPathActive(location.pathname, item.href)}
                      size="md"
                      tooltipPlacement="right"
                      onClick={(e) => handleNavClick(e as unknown as MouseEvent, item.href)}
                    />
                  ) : (
                    <NavItemBase
                      type="link"
                      href={item.href}
                      icon={item.icon}
                      current={isPathActive(location.pathname, item.href)}
                      onClick={(e) => handleNavClick(e as unknown as MouseEvent, item.href)}
                    >
                      {t(item.labelKey)}
                    </NavItemBase>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* User menu at bottom */}
      <div className="border-t border-gray-100 p-3 dark:border-gray-800">
        <UserMenu collapsed={isCollapsed} />
      </div>
    </aside>
  )
}

export default Sidebar
